import { useDebounceFn } from '@vueuse/core';
import { ref, toRef, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useServices } from '@/core/composables/services';
import {
  type ContentServiceDictionariesQuery,
  type ContentServiceQuestionnaireAnswersQuery,
  type ContentServiceQuestionnaireMoveToNextStepMutationVariables,
  type ContentServiceQuestionnaireQuery,
  type ContentServiceQuestionnaireSaveAnswersInput,
  ContentServiceQuestionnaireStatusView,
  QuestionnaireMasterStep,
  useContentServiceDictionariesQuery,
  useContentServiceQuestionnaireAnswersQuery,
  useContentServiceQuestionnaireAnswersValidationsByIdQuery,
  useContentServiceQuestionnaireCancelEditMutation,
  useContentServiceQuestionnaireCreateMutation,
  useContentServiceQuestionnaireEditMutation,
  useContentServiceQuestionnaireFinalizeMutation,
  useContentServiceQuestionnaireMoveToNextStepMutation,
  useContentServiceQuestionnaireQuery,
  useContentServiceQuestionnaireSaveMutation,
} from '@/graphql/generated';
import { DeleteContentImage, GetContentServicImage, UploadContentImage } from '@/parts/content/gql/contentService';
import {
  ContentSection,
  type DeleteContentImageResponse,
  type DeleteContentImageVariables,
  type UploadContentImageResponse,
  type UploadContentImageVariables,
} from '@/parts/content/types';

const dictionaries = ref<ContentServiceDictionariesQuery['contentServiceDictionaries']>();
const questionnaire = ref<ContentServiceQuestionnaireQuery['contentServiceQuestionnaire']>();
const questionnaireAnswers = ref<ContentServiceQuestionnaireAnswersQuery['contentServiceQuestionnaireAnswersById']>();

const completedSteps = ref(-1);
const activeStep = ref(0);
const needValidate = ref(false);

const filesCountBySection = ref<Record<ContentSection, number>>({
  [ContentSection.CommonInformation]: 0,
  [ContentSection.Equipment]: 0,
  [ContentSection.Rooms]: 0,
  [ContentSection.FoodObjects]: 0,
  [ContentSection.Services]: 0,
  [ContentSection.ConferenceRooms]: 0,
  [ContentSection.RestaurantsAndEvents]: 0,
  [ContentSection.SportAndEntertainments]: 0,
  [ContentSection.ForChildren]: 0,
});

const filesCountBySectionObject = ref<Record<string, Record<string, number>>>({
  [ContentSection.ConferenceRooms]: {},
  [ContentSection.Rooms]: {},
  [ContentSection.FoodObjects]: {},
  [ContentSection.Equipment]: {},
});

const maxFilesLimit = 100;

const sectionNames: Record<ContentSection, string> = {
  [ContentSection.CommonInformation]: 'Основная информация',
  [ContentSection.Equipment]: 'Инфраструктура',
  [ContentSection.Rooms]: 'Номера',
  [ContentSection.FoodObjects]: 'Питание',
  [ContentSection.Services]: 'Услуги и развлечения',
  [ContentSection.ConferenceRooms]: 'Конференц-услуги',
  [ContentSection.RestaurantsAndEvents]: 'Рестораны и мероприятия',
  [ContentSection.SportAndEntertainments]: 'Спорт и развлечения',
  [ContentSection.ForChildren]: 'Для детей',
};

const initFilesCountFromAnswers = (answers: any) => {
  if (!answers) return;
  
  Object.values(ContentSection).forEach(section => {
    filesCountBySection.value[section] = 0;
    if (filesCountBySectionObject.value[section]) {
      filesCountBySectionObject.value[section] = {};
    }
  });
  
  filesCountBySection.value[ContentSection.CommonInformation] = 
    answers.generalInfo?.attachments?.length || 0;
  
  if (answers.infrastructureInfo?.pools) {
    answers.infrastructureInfo.pools.forEach((pool: any) => {
      const count = pool.attachments?.length || 0;
      filesCountBySectionObject.value[ContentSection.Equipment][pool.poolId] = count;
      filesCountBySection.value[ContentSection.Equipment] += count;
    });
  }
  
  if (answers.infrastructureInfo?.saunas) {
    answers.infrastructureInfo.saunas.forEach((sauna: any) => {
      const count = sauna.attachments?.length || 0;
      filesCountBySectionObject.value[ContentSection.Equipment][sauna.saunaId] = count;
      filesCountBySection.value[ContentSection.Equipment] += count;
    });
  }
  
  const infrastructureAttachmentsCount = answers.infrastructureInfo?.attachments?.length || 0;
  filesCountBySectionObject.value[ContentSection.Equipment]['__general__'] = infrastructureAttachmentsCount;
  filesCountBySection.value[ContentSection.Equipment] += infrastructureAttachmentsCount;
  
  if (answers.roomCategories) {
    answers.roomCategories.forEach((room: any) => {
      const count = room.attachments?.length || 0;
      filesCountBySectionObject.value[ContentSection.Rooms][room.roomCategoryCid] = count;
      filesCountBySection.value[ContentSection.Rooms] += count;
    });
  }
  
  if (answers.nutritionInfo?.foodObjects) {
    answers.nutritionInfo.foodObjects.forEach((obj: any) => {
      const count = obj.attachments?.length || 0;
      filesCountBySectionObject.value[ContentSection.FoodObjects][obj.foodObjectId] = count;
      filesCountBySection.value[ContentSection.FoodObjects] += count;
    });
  }
  
  if (answers.conferenceServices?.conferenceRooms) {
    answers.conferenceServices.conferenceRooms.forEach((room: any) => {
      const count = room.attachments?.length || 0;
      filesCountBySectionObject.value[ContentSection.ConferenceRooms][room.conferenceRoomId] = count;
      filesCountBySection.value[ContentSection.ConferenceRooms] += count;
    });
  }
};

export const useContentStore = () => {
  const $route = useRoute();

  const accommodationObjectCid = toRef(() => $route.params.id as string | undefined);

  const { $api } = useServices();

  const nextStep = () => {
    activeStep.value++;
  };

  const questionnaireStatus = toRef(() => questionnaire.value?.status);
  const isQuestionnaireSent = toRef(() => questionnaireStatus.value === ContentServiceQuestionnaireStatusView.Sent);
  const isQuestionnaireDraft = toRef(() => questionnaireStatus.value === ContentServiceQuestionnaireStatusView.Draft);

  const totalFilesCount = computed(() => {
    return Object.values(filesCountBySection.value).reduce((sum, count) => sum + count, 0);
  });

  const updateSectionFilesCount = (section: ContentSection, objectName: string | null | undefined, count: number) => {
    if (objectName && filesCountBySectionObject.value[section]) {
      filesCountBySectionObject.value[section][objectName] = count;
      
      filesCountBySection.value[section] = Object.values(filesCountBySectionObject.value[section])
      .reduce((sum, count) => sum + count, 0);
    
    } else if (!objectName && filesCountBySectionObject.value[section]) {
      filesCountBySectionObject.value[section]['__general__'] = count;
      
      filesCountBySection.value[section] = Object.values(filesCountBySectionObject.value[section])
      .reduce((sum, count) => sum + count, 0);
    
    } else {
      filesCountBySection.value[section] = count;
    }
  };

  const getSectionFilesCount = (section: ContentSection): number => {
    return filesCountBySection.value[section] || 0;
  };

  const getContentServiceDictionaries = async () => {
    const { request } = useContentServiceDictionariesQuery({});

    const response = await request();

    if (response.data) {
      dictionaries.value = response.data.contentServiceDictionaries;
    }
  };

  const createQuestionnaire = async (accommodationObjectCid: string) => {
    const { request } = useContentServiceQuestionnaireCreateMutation({
      input: { accommodationObjectCid },
    });

    const response = await request();

    if (response.data) {
      questionnaireAnswers.value = response.data.contentServiceQuestionnaireCreate.questionnaireAnswers;

      await getQuestionnaire();
    }
  };

  const saveQuestionnaire = async (
    input: Omit<ContentServiceQuestionnaireSaveAnswersInput['answers'], 'isFinished'>,
  ) => {
    if (!accommodationObjectCid.value || isQuestionnaireSent.value) {
      return;
    }

    const { request } = useContentServiceQuestionnaireSaveMutation({
      input: { answers: { ...input, isFinished: false }, accommodationObjectCid: accommodationObjectCid.value },
    });

    const response = await request();

    if (response.data) {
      questionnaireAnswers.value = response.data.contentServiceQuestionnaireSaveAnswers.questionnaireAnswers;
    }
  };

  const saveQuestionnaireDebounced = useDebounceFn(saveQuestionnaire, 2500);

  const setActiveStep = () => {
    setActiveStepByStatus(questionnaireAnswers.value?.step);
    setCompletedStepsByStatus(questionnaireAnswers.value?.step);
  };

  const setActiveStepByStatus = (status: QuestionnaireMasterStep | null | undefined) => {
    switch (status) {
      case QuestionnaireMasterStep.GeneralInfo:
        activeStep.value = 0;
        break;
      case QuestionnaireMasterStep.RoomCategories:
        activeStep.value = 1;
        break;
      case QuestionnaireMasterStep.InfrastructureInfo:
        activeStep.value = 2;
        break;
      case QuestionnaireMasterStep.NutritionInfo:
        activeStep.value = 3;
        break;
      case QuestionnaireMasterStep.ServicesAndEntertainment:
        activeStep.value = 4;
        break;
      case QuestionnaireMasterStep.Treatment:
        activeStep.value = 5;
        break;
      case QuestionnaireMasterStep.ConferenceServices:
        activeStep.value = 6;
        break;
      case QuestionnaireMasterStep.Finish:
        activeStep.value = 7;
        break;
    }
  };

  const setCompletedStepsByStatus = (status: QuestionnaireMasterStep | null | undefined) => {
    switch (status) {
      case QuestionnaireMasterStep.GeneralInfo:
        completedSteps.value = -1;
        break;
      case QuestionnaireMasterStep.RoomCategories:
        completedSteps.value = 0;
        break;
      case QuestionnaireMasterStep.InfrastructureInfo:
        completedSteps.value = 1;
        break;
      case QuestionnaireMasterStep.NutritionInfo:
        completedSteps.value = 2;
        break;
      case QuestionnaireMasterStep.ServicesAndEntertainment:
        completedSteps.value = 3;
        break;
      case QuestionnaireMasterStep.Treatment:
        completedSteps.value = 4;
        break;
      case QuestionnaireMasterStep.ConferenceServices:
        completedSteps.value = 5;
        break;
      case QuestionnaireMasterStep.Finish:
        completedSteps.value = 6;
        break;
    }
  };

  const back = () => {
    activeStep.value--;
  };

  const getQuestionnaire = async () => {
    if (!accommodationObjectCid.value) {
      return;
    }

    questionnaire.value = undefined;

    const { request } = useContentServiceQuestionnaireQuery({
      accommodationObjectCid: accommodationObjectCid.value,
    });

    const response = await request();

    if (response.data) {
      if (response.data.contentServiceQuestionnaire === null) {
        await createQuestionnaire(accommodationObjectCid.value);
      } else {
        questionnaire.value = response.data.contentServiceQuestionnaire;
      }
    }
  };

  const getQuestionnaireAnswers = async () => {
    if (!questionnaire.value) {
      return;
    }

    questionnaireAnswers.value = undefined;

    const { request } = useContentServiceQuestionnaireAnswersQuery({ questionnaireId: questionnaire.value.id });

    const response = await request();

    if (response.data) {
      questionnaireAnswers.value = response.data.contentServiceQuestionnaireAnswersById;
      initFilesCountFromAnswers(questionnaireAnswers.value);
      setActiveStep();
    }
  };

  const checkQuestionnaireStepsValidation = async () => {
    if (!questionnaire.value) {
      return;
    }

    const { request } = useContentServiceQuestionnaireAnswersValidationsByIdQuery({
      questionnaireId: questionnaire.value.id,
    });

    const response = await request();

    if (response.data) {
      const firstInvalidStep = response.data.contentServiceQuestionnaireAnswersValidationsById?.validations?.find(
        (x) => !x.isValid,
      );
      if (firstInvalidStep) {
        setActiveStepByStatus(firstInvalidStep.step);
        needValidate.value = true;
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  };

  const completeValidation = async () => {
    needValidate.value = false;
  };

  const uploadContentImage = async (input: {
    accommodationObjectCid: string;
    section: ContentSection;
    sectionObjectName: string | null | undefined;
    attachment: File;
  }) => {
    const response = await $api.mutation<UploadContentImageResponse, UploadContentImageVariables>(UploadContentImage, {
      ...input,
    });

    return response;
  };

  const getContentServiceImage = async (input: {
    fileId: string;
    section: ContentSection;
    sectionObjectName: string | null | undefined;
    accommodationObjectCid: string;
  }) => {
    const response = await $api.query<{ GetContentServicImage: { data: Blob } }>(GetContentServicImage, { ...input });

    return response;
  };

  const deleteContentImage = async (input: DeleteContentImageVariables) => {
    const response = await $api.mutation<DeleteContentImageResponse, DeleteContentImageVariables>(DeleteContentImage, {
      ...input,
    });

    return response;
  };

  const moveToNextStep = async (
    finishedStep: QuestionnaireMasterStep,
    answers: Omit<ContentServiceQuestionnaireMoveToNextStepMutationVariables['answers'], 'isFinished'>,
  ) => {
    if (!accommodationObjectCid.value) {
      return;
    }

    const { request } = useContentServiceQuestionnaireMoveToNextStepMutation({
      accommodationObjectCid: accommodationObjectCid.value,
      answers: {
        ...answers,
        isFinished: false,
      },
      finishedStep,
    });

    const response = await request();

    if (response.data) {
      questionnaireAnswers.value = response.data.contentServiceQuestionnaireMoveToNextStep.questionnaireAnswers;
      activeStep.value++;
      setCompletedStepsByStatus(questionnaireAnswers.value?.step);
    }
  };

  const sendQuestionnaire = async () => {
    if (!accommodationObjectCid.value || !questionnaireAnswers.value || !questionnaire.value) {
      return;
    }

    if (!(await checkQuestionnaireStepsValidation())) {
      return;
    }

    const { request } = useContentServiceQuestionnaireFinalizeMutation({
      accommodationObjectCid: accommodationObjectCid.value,
      answers: questionnaireAnswers.value,
    });

    const response = await request();

    if (response.data) {
      questionnaire.value.status = ContentServiceQuestionnaireStatusView.Sent;
    }

    return response;
  };

  const clearQuestionnaire = () => {
    questionnaire.value = null;
  };

  const editQuestionnaire = async () => {
    if (!accommodationObjectCid.value) {
      return;
    }

    const { request } = useContentServiceQuestionnaireEditMutation({
      accommodationObjectCid: accommodationObjectCid.value,
    });

    const response = await request();

    if (response.data) {
      questionnaireAnswers.value = response.data.contentServiceQuestionnaireEdit.questionnaireAnswers;

      await getQuestionnaire();

      setCompletedStepsByStatus(questionnaireAnswers.value?.step);
    }
  };

  const cancelEditQuestionnaire = async () => {
    if (!questionnaire.value) {
      return;
    }

    const { request } = useContentServiceQuestionnaireCancelEditMutation({
      questionnaireId: questionnaire.value.id,
    });

    const response = await request();

    if (response.data) {
      await getQuestionnaire();
      await getQuestionnaireAnswers();
    }
  };

  return {
    dictionaries,
    questionnaire,
    questionnaireAnswers,
    getQuestionnaireAnswers,
    completedSteps,
    activeStep,
    getContentServiceDictionaries,
    saveQuestionnaire,
    saveQuestionnaireDebounced,
    getQuestionnaire,
    uploadContentImage,
    createQuestionnaire,
    getContentServiceImage,
    deleteContentImage,
    moveToNextStep,
    sendQuestionnaire,
    back,
    nextStep,
    questionnaireStatus,
    isQuestionnaireSent,
    isQuestionnaireDraft,
    clearQuestionnaire,
    needValidate,
    completeValidation,
    editQuestionnaire,
    cancelEditQuestionnaire,
    totalFilesCount,
    maxFilesLimit,
    sectionNames,
    getSectionFilesCount,
    updateSectionFilesCount,
  };
};
